import React, { Dispatch, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl/dist';

import ApplicationsList from '../applicationsList/ApplicationsList';
import YearSelector from '../yearSelector/YearSelector';
import {
    ConditionalRendering,
    useAppSelector,
    useAppDispatch,
    fetchApplicationsDetails,
    setCurrentYear,
    environmentSelector,
} from '../../utils';

import {
    StyledLabel,
    StyledHeader,
    StyledLabelNoData,
    StyledContainer,
} from './MemberApplicationsListContainer.styles';

function MemberApplicationsListContainer() {
    const [isLoading, setIsLoading] = useState(true);
    const dispatch: Dispatch<any> = useAppDispatch();
    const environment = useAppSelector(environmentSelector);
    const currentYear = useAppSelector(state => state.capApplications.currentYear);
    const { currentApplications, pastApplications, queuedApplications } = useAppSelector(
        state => state.capApplications.capApplicationsInfo,
    );
    const appCoverageYears = useAppSelector(state => state.capApplications.appControllerInfo.appCoverageYears);
    const hasCurrentApplicationsData = currentApplications.length === 0 && pastApplications.length > 0;
    const hasCurrentPastQueuedAppData =
        currentApplications.length === 0 && pastApplications.length === 0 && queuedApplications.length === 0;

    useEffect(() => {
        if (currentYear) {
            setIsLoading(true);
            dispatch(fetchApplicationsDetails(currentYear, environment, () => { setIsLoading(false) }));
        }
    }, [currentYear]);

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(setCurrentYear(event.target.value));
    };

    if (isLoading) {
        return null;
    }
    
    return (
        <StyledContainer className="desktop:grid-col-9 member-applications-container" id="main-content">
            <StyledHeader className="header">
                <StyledLabel>
                    <FormattedMessage id="cap.applications.header.currentApplications" />
                </StyledLabel>
                <YearSelector years={appCoverageYears} currentYear={currentYear} onYearChange={handleYearChange} />
            </StyledHeader>
            <ConditionalRendering isTrue={hasCurrentApplicationsData}>
                <>
                    <ApplicationsList applications={currentApplications} applicationListId="no-current-application" />
                    <StyledLabelNoData>
                        <FormattedMessage id="cap.applications.message.noCurrentAppData" />
                    </StyledLabelNoData>
                </>
            </ConditionalRendering>

            <ConditionalRendering isTrue={currentApplications.length > 0}>
                <ApplicationsList applications={currentApplications} applicationListId="current-application" />
            </ConditionalRendering>

            <ConditionalRendering isTrue={hasCurrentPastQueuedAppData}>
                <StyledLabelNoData>
                    <FormattedMessage id="cap.applications.message.noData" />
                </StyledLabelNoData>
            </ConditionalRendering>

            <ConditionalRendering isTrue={pastApplications.length > 0}>
                <>
                    <StyledHeader className="header">
                        <StyledLabel>
                            <FormattedMessage id="cap.applications.header.pastApplications" />
                        </StyledLabel>
                        <YearSelector currentYear={currentYear} />
                    </StyledHeader>
                    <ApplicationsList applications={pastApplications} applicationListId="past-application" />
                </>
            </ConditionalRendering>

            <ConditionalRendering isTrue={queuedApplications.length > 0}>
                <>
                    <StyledHeader className="header">
                        <StyledLabel>
                            <FormattedMessage id="cap.applications.header.queuedApplications" />
                        </StyledLabel>
                        <YearSelector currentYear={currentYear} />
                    </StyledHeader>
                    <ApplicationsList applications={queuedApplications} applicationListId="queued-application" />
                </>
            </ConditionalRendering>
        </StyledContainer>
    );
}

export default MemberApplicationsListContainer;
